/**
 * Public Learning Packages Browse Page
 * Card grid of published learning packages with item counts, name search,
 * language filter, an info modal per package (rich description + contents),
 * and the visitor's anonymous tracking key for sharing with trainers.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card, Col, Input, Modal, Row, Select, Spin, Tag, Tooltip, Typography, message } from 'antd';
import {
  ApartmentOutlined,
  AppstoreAddOutlined,
  ArrowRightOutlined,
  ExperimentOutlined,
  GlobalOutlined,
  InfoCircleOutlined,
  KeyOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import PagesWrapper from '../../../PagesWrapper';
import { useAuth } from '../../../context/AuthContext';
import { getPublicLearningPackages } from '../../../services/learningPackagesService';
import { getAnonymousVisitorKey } from '../../../utils/anonymousVisitorKey';
import styles from './LearningPackagesPublic.module.css';

const { Search } = Input;
const { Title, Text } = Typography;

/**
 * Renders the public browse grid for published learning packages.
 * @returns {JSX.Element} Public packages list page
 */
const PackagesPublicList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [languageFilter, setLanguageFilter] = useState(null);
  const [infoPackage, setInfoPackage] = useState(null);

  const loadPackages = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getPublicLearningPackages({ suppressNotification: true });
      setItems(result || []);
    } catch (error) {
      message.error(t('learningPackages.publicFetchError', 'Failed to load learning packages'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  const languageOptions = useMemo(() => {
    const languages = [...new Set(items.map(pkg => pkg.countryLanguage).filter(Boolean))].sort();
    return languages.map(language => ({ value: language, label: language }));
  }, [items]);

  const filteredItems = useMemo(() => items.filter(pkg => {
    if (languageFilter && pkg.countryLanguage !== languageFilter) return false;
    if (search && !pkg.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [items, search, languageFilter]);

  const visitorKey = getAnonymousVisitorKey();

  return (
    <PagesWrapper>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <AppstoreAddOutlined className={styles.titleIcon} />
            <Title level={2} className={styles.title}>{t('learningPackages.publicListTitle', 'Learning Packages')}</Title>
          </div>
          <Text type="secondary" className={styles.subtitle}>
            {t('learningPackages.publicListSubtitle', 'Curated learning journeys combining learning paths, quizzes, and simulations')}
          </Text>
        </div>

        {!isAuthenticated && (
          <Tooltip title={t('learningPackages.yourKeyHint', 'Share this key with your trainer so they can recognize your progress. It is not a login and grants no access.')}>
            <div className={styles.keyBar}>
              <KeyOutlined className={styles.keyBarIcon} />
              <span>{t('learningPackages.yourKeyLabel', 'Your tracking key')}:</span>
              <Text code copyable={{ text: visitorKey }}>{visitorKey}</Text>
            </div>
          </Tooltip>
        )}

        <div className={styles.toolsRow}>
          <Search
            placeholder={t('learningPackages.publicSearchPlaceholder', 'Search by name...')}
            allowClear
            onSearch={setSearch}
            onChange={(event) => { if (!event.target.value) setSearch(''); }}
            style={{ width: 280 }}
          />
          <Select
            allowClear
            placeholder={t('learningPackages.allLanguages', 'All languages')}
            options={languageOptions}
            value={languageFilter}
            onChange={setLanguageFilter}
            style={{ width: 200 }}
            suffixIcon={<GlobalOutlined />}
          />
        </div>

        <Spin spinning={loading}>
          {filteredItems.length === 0 && !loading ? (
            <div className={styles.emptyState}>
              {t('learningPackages.publicListEmpty', 'No published learning packages yet')}
            </div>
          ) : (
            <Row gutter={[16, 16]} className={styles.packageGrid}>
              {filteredItems.map(pkg => (
                <Col xs={24} sm={12} lg={8} key={pkg.id}>
                  <Card className={styles.packageCard} onClick={() => navigate(`/packages/${pkg.id}`)}>
                    <Tooltip title={t('learningPackages.packageInfo', 'About this package')}>
                      <Button
                        className={styles.infoButton}
                        type="text"
                        shape="circle"
                        icon={<InfoCircleOutlined style={{ color: '#1890ff' }} />}
                        onClick={(event) => {
                          event.stopPropagation();
                          setInfoPackage(pkg);
                        }}
                      />
                    </Tooltip>
                    <div className={styles.packageCardTitle}>{pkg.name}</div>
                    <div className={styles.packageCardCounts}>
                      {pkg.countryLanguage && (
                        <Tag icon={<GlobalOutlined />}>{pkg.countryLanguage}</Tag>
                      )}
                      {pkg.pathCount > 0 && (
                        <Tag color="blue">{pkg.pathCount} {t('learningPackages.paths', 'Paths')}</Tag>
                      )}
                      {pkg.quizCollectionCount > 0 && (
                        <Tag color="purple">{pkg.quizCollectionCount} {t('learningPackages.quizzes', 'Quizzes')}</Tag>
                      )}
                      {pkg.trainingProfileCount > 0 && (
                        <Tag color="cyan">{pkg.trainingProfileCount} {t('learningPackages.profiles', 'Profiles')}</Tag>
                      )}
                    </div>
                    <Button type="link" style={{ padding: 0 }}>
                      {t('learningPackages.explorePackage', 'Explore Package')} <ArrowRightOutlined />
                    </Button>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Spin>

        <Modal
          title={infoPackage?.name}
          open={!!infoPackage}
          onCancel={() => setInfoPackage(null)}
          footer={[
            <Button key="close" onClick={() => setInfoPackage(null)}>
              {t('common.close', 'Close')}
            </Button>,
            <Button
              key="explore"
              type="primary"
              icon={<ArrowRightOutlined />}
              onClick={() => {
                navigate(`/packages/${infoPackage.id}`);
                setInfoPackage(null);
              }}
            >
              {t('learningPackages.explorePackage', 'Explore Package')}
            </Button>,
          ]}
        >
          {infoPackage && (
            <>
              {infoPackage.descriptionRichContent && (
                <div
                  className={styles.descriptionContent}
                  // Rich text authored by content creators through the trusted editor pipeline.
                  dangerouslySetInnerHTML={{ __html: infoPackage.descriptionRichContent }}
                />
              )}
              {(infoPackage.pathNames || []).length > 0 && (
                <>
                  <div className={styles.infoModalSection}>
                    <ApartmentOutlined style={{ color: '#1890ff' }} /> {t('learningPackages.areaPaths', 'Learning Paths')}
                  </div>
                  {infoPackage.pathNames.map((name, index) => (
                    <div key={index} className={styles.infoModalItem}>{name}</div>
                  ))}
                </>
              )}
              {(infoPackage.quizCollectionNames || []).length > 0 && (
                <>
                  <div className={styles.infoModalSection}>
                    <QuestionCircleOutlined style={{ color: '#722ed1' }} /> {t('learningPackages.areaQuizzes', 'Quiz Collections')}
                  </div>
                  {infoPackage.quizCollectionNames.map((name, index) => (
                    <div key={index} className={styles.infoModalItem}>{name}</div>
                  ))}
                </>
              )}
              {(infoPackage.trainingProfileNames || []).length > 0 && (
                <>
                  <div className={styles.infoModalSection}>
                    <ExperimentOutlined style={{ color: '#13c2c2' }} /> {t('learningPackages.areaProfiles', 'Training Profiles')}
                  </div>
                  {infoPackage.trainingProfileNames.map((name, index) => (
                    <div key={index} className={styles.infoModalItem}>{name}</div>
                  ))}
                </>
              )}
            </>
          )}
        </Modal>
      </div>
    </PagesWrapper>
  );
};

export default PackagesPublicList;
